/**
 * Dependency-free heat grid (rows × cols, one tinted cell each) for the weekday × hour
 * view. Plain CSS grid, not SVG: cells need to stay square-ish and readable at 390 px,
 * which a grid does for free and a fixed viewBox does not. Cell tint is the value's
 * share of the grid max; a missing or zero cell stays the paper grey so empty hours read
 * as empty, not as "small".
 */

export interface HeatGridProps {
  rows: readonly string[]
  cols: readonly string[]
  /** Keyed `${row}|${col}` -- the same key `heatKey` builds. */
  values: ReadonlyMap<string, number>
  /** Solid hex the max cell is painted in; lower values fade towards white. */
  color: string
  rowLabel?: (row: string) => string
  colLabel?: (col: string) => string
  formatValue?: (v: number) => string
  ariaLabel: string
}

export function heatKey(row: string, col: string): string {
  return `${row}|${col}`
}

export function HeatGrid({
  rows, cols, values, color, rowLabel, colLabel, formatValue, ariaLabel,
}: HeatGridProps): JSX.Element {
  const fmt = formatValue ?? ((v: number) => v.toLocaleString('en-IN'))
  let max = 0
  for (const v of values.values()) if (Number.isFinite(v) && v > max) max = v
  const total = [...values.values()].reduce((s, v) => s + (Number.isFinite(v) ? v : 0), 0)

  if (!rows.length || !cols.length || total === 0) {
    return <p className="py-6 text-center text-[11px] text-slate-400">No data in this window.</p>
  }

  return (
    <div role="img" aria-label={ariaLabel} className="overflow-x-auto">
      <div
        className="grid min-w-[520px] gap-[2px]"
        style={{ gridTemplateColumns: `max-content repeat(${cols.length}, minmax(0, 1fr))` }}
      >
        <span aria-hidden="true" />
        {cols.map((c) => (
          <span key={c} className="truncate text-center font-mono text-[8.5px] text-slate-400" aria-hidden="true">
            {colLabel ? colLabel(c) : c}
          </span>
        ))}
        {rows.map((r) => (
          <RowCells
            key={r}
            row={r}
            cols={cols}
            values={values}
            max={max}
            color={color}
            label={rowLabel ? rowLabel(r) : r}
            colLabel={colLabel}
            fmt={fmt}
          />
        ))}
      </div>
      <div className="mt-2 flex items-center justify-end gap-1.5 text-[9.5px] text-slate-400">
        <span>0</span>
        <span className="h-2 w-16 rounded-sm" style={{ background: `linear-gradient(90deg, #F1F5F9, ${color})` }} />
        <span className="font-mono tabular-nums">{fmt(max)}</span>
      </div>
    </div>
  )
}

function RowCells({
  row, cols, values, max, color, label, colLabel, fmt,
}: {
  row: string
  cols: readonly string[]
  values: ReadonlyMap<string, number>
  max: number
  color: string
  label: string
  colLabel?: (col: string) => string
  fmt: (v: number) => string
}): JSX.Element {
  return (
    <>
      <span className="pr-2 font-mono text-[9.5px] leading-[18px] text-slate-500">{label}</span>
      {cols.map((c) => {
        const raw = values.get(heatKey(row, c))
        const v = typeof raw === 'number' && Number.isFinite(raw) ? raw : 0
        // Floor the tint at 12% so a cell with one alert is still visibly "on".
        const alpha = v > 0 && max > 0 ? Math.max(0.12, v / max) : 0
        return (
          <span
            key={c}
            className="h-[18px] rounded-[3px] bg-slate-100"
            style={alpha > 0 ? { background: color, opacity: alpha } : undefined}
            title={`${label} ${colLabel ? colLabel(c) : c}: ${fmt(v)}`}
          />
        )
      })}
    </>
  )
}
